/** Snapshot of the app shell scroll container (+ window fallback). */
export type ScrollSnapshot = {
  mainTop: number
  windowY: number
  /** Distance from scrollport top to an anchored row (keeps item stable when layout grows). */
  anchorOffset: number | null
}

/**
 * Walk ancestors for the nearest vertical scrollport.
 * Prefers Layout's `<main class="shell-main-pad">` used by the mobile/PWA shell.
 */
export function getScrollParent(node: Element | null): HTMLElement | null {
  if (typeof document === 'undefined') return null

  let el: HTMLElement | null = node instanceof HTMLElement ? node : node?.parentElement ?? null
  while (el) {
    const style = window.getComputedStyle(el)
    const oy = style.overflowY
    if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
      return el
    }
    el = el.parentElement
  }

  return document.querySelector('main.shell-main-pad') as HTMLElement | null
}

function resolveScrollParent(fromEl?: Element | null): HTMLElement | null {
  return (
    getScrollParent(fromEl ?? null) ||
    (document.querySelector('main.shell-main-pad') as HTMLElement | null)
  )
}

export function captureScroll(
  fromEl?: Element | null,
  anchorEl?: Element | null
): ScrollSnapshot {
  const main = resolveScrollParent(fromEl)
  let anchorOffset: number | null = null
  if (main && anchorEl instanceof HTMLElement) {
    anchorOffset =
      anchorEl.getBoundingClientRect().top - main.getBoundingClientRect().top
  }
  return {
    mainTop: main?.scrollTop ?? 0,
    windowY: window.scrollY || document.documentElement.scrollTop || 0,
    anchorOffset,
  }
}

export function restoreScroll(
  snapshot: ScrollSnapshot | null,
  fromEl?: Element | null,
  anchorEl?: Element | null
): void {
  if (!snapshot || typeof document === 'undefined') return
  const main = resolveScrollParent(fromEl)
  if (main) {
    main.scrollTop = snapshot.mainTop
    // Prefer keeping the photographed row at the same viewport offset once layout settles
    // (thumbnail insert, progress text, parent re-render). Absolute scrollTop alone drifts.
    if (anchorEl instanceof HTMLElement && snapshot.anchorOffset != null) {
      const delta =
        anchorEl.getBoundingClientRect().top -
        main.getBoundingClientRect().top -
        snapshot.anchorOffset
      if (Math.abs(delta) > 1) {
        main.scrollTop += delta
      }
    }
  }
  if (snapshot.windowY > 0 || window.scrollY !== snapshot.windowY) {
    window.scrollTo(0, snapshot.windowY)
  }
}
